import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { Heart, Share2, Store, Ruler, Check, Minus, Plus, Sparkles, ChevronRight } from "lucide-react";
import SaveButton from "../components/SaveButton";
import WholesaleSlabsViewer from "../components/WholesaleSlabsViewer";
import ColorSwatchPicker from "../components/ColorSwatchPicker";
import { ProductCard } from "./HomePage";

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setData(data);
      if (data.product.sizes?.length === 1) setSize(data.product.sizes[0]);
    })();
    // Fetch similar products in parallel — embedding-based AI recommendations
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}/similar`, { params: { limit: 8 } });
        setSimilar(data.items || []);
      } catch { setSimilar([]); }
    })();
    // Track view (best-effort, only when authenticated)
    if (user) {
      api.post(`/me/views/${id}`).catch(() => {});
    }
  }, [id, user]);

  const handleAdd = async (buyNow = false) => {
    if (!data) return;
    if (data.product.sizes?.length && !size) {
      alert("Please select a size");
      return;
    }
    if (data.product.colors?.length && !color) {
      alert("Please select a colour");
      return;
    }
    setAdding(true);
    try {
      await addItem(data.product.product_id, size, qty, color);
      setAdded(true);
      setTimeout(()=>setAdded(false), 1500);
      if (buyNow) nav("/cart");
    } finally { setAdding(false); }
  };

  if (!data) return <div className="p-10 text-center text-[#A1A1AA]">Loading…</div>;
  const p = data.product;
  const stock = size ? (p.stock?.[size] || 0) : null;

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-[#261534]">
            {p.images?.[activeImg] && <img src={p.images[activeImg]} className="w-full h-full object-cover"/>}
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((im, i) => (
                <button key={`${im}-${i}`} onClick={()=>setActiveImg(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${i===activeImg?"border-[#D4FF00]":"border-transparent"}`}>
                  <img src={im} className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {data.store && (
            <Link to={`/store/${data.store.store_id}`} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#D4FF00] mb-2">
              <Store size={14}/> {data.store.name}
            </Link>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl mb-2 uppercase leading-tight">{p.name}</h1>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-display font-black text-4xl text-[#D4FF00]">{fmtMoney(p.price)}</span>
            <span className="text-sm text-[#A1A1AA]">★ {p.rating} · {p.sales} sold</span>
          </div>
          <p className="text-[#A1A1AA] mb-6">{p.description}</p>

          {p.sizes?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-widest">Size</span>
                <button className="text-xs text-[#A1A1AA] flex items-center gap-1 hover:text-[#D4FF00]">
                  <Ruler size={12}/> Size guide
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map(s => {
                  const out = (p.stock?.[s] || 0) === 0;
                  return (
                    <button key={s} data-testid={`size-${s}`} disabled={out} onClick={()=>setSize(s)}
                      className={`min-w-[56px] px-4 py-3 rounded-xl border-2 text-sm font-bold uppercase transition ${
                        out ? "opacity-30 line-through cursor-not-allowed border-[#3D2845]" :
                        size===s ? "border-[#D4FF00] bg-[#D4FF00]/10 text-[#D4FF00]" :
                        "border-[#3D2845] hover:border-[#D4FF00]"}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
              {size && <div className="text-xs text-[#A1A1AA] mt-2">{stock} in stock</div>}
            </div>
          )}

          {p.colors?.length > 0 && (
            <div className="mb-6" data-testid="product-colors-section">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-widest">Colour</span>
                {color && (
                  <span className="text-[10px] text-[#D4FF00] font-bold uppercase tracking-widest">
                    Selected: {color}
                  </span>
                )}
              </div>
              <ColorSwatchPicker
                colors={p.colors}
                value={color}
                onChange={setColor}
                testidPrefix="product-color"
                size="lg"/>
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#261534] border border-[#3D2845] rounded-full px-2">
              <button data-testid="qty-minus" onClick={()=>setQty(Math.max(1, qty-1))} className="w-8 h-8 flex items-center justify-center hover:text-[#D4FF00]"><Minus size={14}/></button>
              <span className="font-bold w-6 text-center" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={()=>setQty(qty+1)} className="w-8 h-8 flex items-center justify-center hover:text-[#D4FF00]"><Plus size={14}/></button>
            </div>
          </div>

          <WholesaleSlabsViewer
            productId={p.product_id}
            slabs={p.wholesale_slabs}
            qty={qty}
            unitPrice={p.price}/>

          {/* Pre-order banner */}
          {p.is_advance_booking && (
            <div className="mb-4 p-3 rounded-xl border border-[#FF007F]/40 bg-[#FF007F]/10"
              data-testid="product-pre-order-banner">
              <div className="text-[10px] uppercase font-bold tracking-widest text-[#FF007F] mb-1">
                📦 Pre-order
              </div>
              <div className="text-sm">
                {p.expected_ship_date
                  ? <>Ships on <span className="font-bold">{new Date(p.expected_ship_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>.</>
                  : <>Ships when this drop releases.</>}
                {" Lock yours in now."}
              </div>
              {p.advance_booking_closes_at && (
                <div className="text-[10px] text-[#A1A1AA] mt-1">
                  Pre-order window closes {new Date(p.advance_booking_closes_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          {p.is_new_launch && !p.is_advance_booking && (
            <div className="mb-4 p-2 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-xs flex items-center gap-2"
              data-testid="product-new-launch-banner">
              <span className="text-[10px] uppercase font-bold tracking-widest text-[#00F0FF]">🚀 NEW LAUNCH</span>
              {p.launch_date && <span className="text-[#A1A1AA]">{new Date(p.launch_date).toLocaleDateString(undefined, { month: "long", day: "numeric" })}</span>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button data-testid="add-to-cart-btn" disabled={adding} onClick={()=>handleAdd(false)} className="btn-secondary py-4 font-display uppercase tracking-wider">
              {added ? <><Check size={16} className="inline mr-1"/>Added</> : (p.is_advance_booking ? "Pre-order" : "Add to cart")}
            </button>
            <button data-testid="buy-now-btn" disabled={adding} onClick={()=>handleAdd(true)} className="btn-primary py-4">
              {p.is_advance_booking ? "Pre-order now" : "Buy now"}
            </button>
          </div>

          <div className="flex gap-3 mt-3">
            <SaveButton kind="product" id={p.product_id}/>
            <button
              onClick={async () => {
                const url = window.location.href;
                try {
                  if (navigator.share) { await navigator.share({ title: p.name, url }); return; }
                } catch (shareErr) {
                  // navigator.share rejects when the user cancels — that's fine.
                  if (process.env.NODE_ENV !== "production") console.debug("[product] share cancelled:", shareErr?.message);
                }
                try {
                  await navigator.clipboard.writeText(url);
                  alert("Link copied 🔗");
                } catch (clipErr) {
                  console.error("[product] clipboard write failed", clipErr);
                  // Last-resort fallback: show the URL so the user can copy it manually.
                  prompt("Copy this link:", url);
                }
              }}
              data-testid="product-share-btn"
              className="chip"
            ><Share2 size={14}/> Share</button>
          </div>
        </div>
      </div>

      {/* AI-powered similar products rail */}
      {similar.length > 0 && (
        <section className="mt-12 sm:mt-16" data-testid="product-similar-rail">
          <div className="flex items-end justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#FF007F]"/>
              <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                You may also <span className="text-[#FF007F]">like</span>
              </h2>
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] hidden sm:inline ml-1">
                · AI picks
              </span>
            </div>
            <Link to="/search" className="text-sm font-bold text-[#A1A1AA] hover:text-[#FF007F] flex items-center gap-1">
              See all <ChevronRight size={14}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {similar.slice(0, 8).map((sp, i) => <ProductCard key={sp.product_id} p={sp} index={i}/>)}
          </div>
        </section>
      )}
    </div>
  );
}
